<template>
  <div class="ibox">
    <div class="ibox-title">
      {{ showTitle }}
      <DownloadIcon v-if="entityOrder.id" @click="download" class="float-right" style="cursor: pointer;"/>
      <button v-if="format !== 'text'" class="btn btn-sm btn-outline float-right mr-2" @click="format = 'text'">Text</button>
      <button v-if="format !== 'json'" class="btn btn-sm btn-outline float-right mr-2" @click="format = 'json'">JSON</button>
    </div>

    <div v-if="format === 'text'" class="ibox-content">
      <div class="order-details">
        <div class="row">
          <div class="col-md-6">
            <div class="detail-group">
              <h4>Info</h4>
              <div class="detail-item">
                <span class="label">ID:</span> 
                <span class="value">{{ entityOrder.id }}</span>
              </div>
              <div class="detail-item">
                <span class="label">UUID:</span> 
                <span class="value">{{ entityOrder.uuid }}</span>
              </div>
              <div class="detail-item">
                <span class="label">App:</span> 
                <span class="value">{{ entityOrder.appName }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Status code:</span> 
                <span class="value status-badge" :class="'status-' + entityOrder.status">{{ entityOrder.status }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Status:</span> 
                <span class="value">{{ statusLabel(entityOrder.status) }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Stage:</span> 
                <span class="value">{{ entityOrder.stageName }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Kind:</span> 
                <span class="value">{{ entityOrder.kind }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="detail-group">
              <h4>Metadata</h4>
              <div class="detail-item">
                <span class="label">Created at:</span> 
                <span class="value">{{ entityOrder.createdAt | datetime }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Updated at:</span> 
                <span class="value">{{ entityOrder.updatedAt | datetime }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Created by:</span> 
                <span class="value">{{ entityOrder.createdBy }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Requested by:</span> 
                <span class="value">{{ entityOrder.requestedBy }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Updated by:</span> 
                <span class="value">{{ entityOrder.updatedBy || '-' }}</span>
              </div>
              <div class="detail-item">
                <span class="label">Account:</span> 
                <span class="value">{{ getAccountName(entityOrder) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="row mt-4" v-if="entityOrder.input">
          <div class="col-md-6">
            <div class="detail-group">
              <h4>Input</h4>
              <div class="detail-item" v-if="entityOrder.input.cnpj">
                <span class="label">CNPJ:</span> 
                <span class="value">{{ entityOrder.input.cnpj | cnpj }}</span>
              </div>
              <div class="detail-item" v-if="entityOrder.input.scrapingServiceName">
                <span class="label">Service:</span> 
                <span class="value">{{ entityOrder.input.scrapingServiceName }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-6">
            <div class="detail-group">
              <h4>Errors</h4>
              <div class="detail-item" v-for="(error, index) in getErrors(entityOrder)" :key="index">
                <span class="label">Error {{ index + 1 }}:</span> 
                <span class="value"><small>{{ error }}</small></span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>

    <div v-if="format === 'json'" class="ibox-content">
      <Panel :data="entityOrder" :deep="3"/>
    </div>

  </div>

</template>

<script>
import DownloadCreator from '@/models/DownloadCreator'
import Panel from '@/components/admin/maestro/Panel'
import { DownloadIcon } from 'vue-feather-icons'

const mapStatus = {
  '0': 'unprocessed',
  '100': 'processing',
  '200': 'processed',
  '300': 'processed/system_error',
  '400': 'processed/user_error',
  '500': 'error'
}

export default {
  name: 'Form',
  components: { DownloadIcon, Panel },
  computed: {
    showTitle() {
      if (!this.entityOrder.id)
        return 'Loading ...'
      return `${this.entityOrder.kind} - ${this.entityOrder.uuid}`
    },
  },
  data: () => ({
    format: 'text'
  }),
  methods: {
    copyToClipboard(text) {
      navigator.clipboard.writeText(text)
        .then(() => this.$notify ? this.$notify.add('Copiado para a área de transferência') : null)
        .catch(err => console.error('Erro ao copiar: ', err))
    },
    download() {
      const string = JSON.stringify(this.entityOrder, null, 2)
      DownloadCreator.download(`order_${this.entityOrder.id}.json`, string)
    },
    statusLabel(status) {
      return mapStatus[status] || status
    },
    getErrors(order) {
      const errors = order.result?.errors
      if (Array.isArray(errors) && errors.length > 0)
        return errors 
      return null
    },
  },
  inject: ['getAccountName'],
  props: {
    entityOrder: {required: true, type: Object},
    title: {required: false, type: String}
  }
}
</script>

<style scoped>

.order-details {
  font-size: 14px;
}

.detail-group {
  margin-bottom: 20px;
}

.detail-group h4 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--border-color);
  color: var(--primary-text-color);
}

.detail-item {
  margin-bottom: 8px;
  display: flex;
  align-items: center;
}

.detail-item .label {
  font-weight: 600;
  min-width: 120px;
  color: var(--secondary-text-color);
}

.detail-item .value {
  color: var(--primary-text-color);
}

.copy-icon {
  margin-left: 8px;
  cursor: pointer;
  color: var(--primary-color);
}

.copy-icon:hover {
  color: var(--primary-color-hover);
}

.status-badge {
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  background-color: var(--light-gray);
  color: var(--primary-text-color);
  border: 1px solid var(--border-color);
}

.status-200 {
  background-color: var(--primary-color);
  color: #ffffff;
  border-color: var(--primary-color);
}

.status-0 {
  background-color: var(--light-gray);
  color: var(--secondary-text-color);
}

.status-100 {
  background-color: var(--attention-color, #FFC866);
  color: #333;
}

.status-300,
.status-400,
.status-500 {
  background-color: var(--secondary-color, #E93F66);
  color: #ffffff;
}

</style>
